import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { userService } from "../../_services";
import ConfirmPopup from "./ConfirmPopup.jsx";
import LinkAccountImg from '../../images/link-account.svg';
import * as z from 'zod';

const wisePaymentSchema = z.object({
    address: z.string()
        .refine(val => val.trim().length > 0, 'Please input street address')
        .refine(val => val.trim().length <= 25, 'Please input valid street address'),
    city: z.string()
        .refine(val => val.trim().length >= 2 && val.trim().length <= 35, 'Please input valid city'),
    state: z.string().min(1, 'Please select State'),
    zip: z.string()
        .refine(val => val.trim().length === 5, 'Please input valid zipcode'),
    routingNumber: z.string()
        .refine(val => val.trim().length === 9, 'Please input valid Routing Number'),
    accountNumber: z.string()
        .refine(val => val.trim().length >= 4 && val.trim().length <= 17, 'Please input valid Account Number'),
    accountType: z.string().min(1, 'Please select Account Type'),
});

const US_STATES = [
    { name: 'Alabama', value: 'AL' },
    { name: 'Alaska', value: 'AK' },
    { name: 'Arizona', value: 'AZ' },
    { name: 'Arkansas', value: 'AR' },
    { name: 'California', value: 'CA' },
    { name: 'Colorado', value: 'CO' },
    { name: 'Connecticut', value: 'CT' },
    { name: 'Delaware', value: 'DE' },
    { name: 'Florida', value: 'FL' },
    { name: 'Georgia', value: 'GA' },
    { name: 'Hawaii', value: 'HI' },
    { name: 'Idaho', value: 'ID' },
    { name: 'Illinois', value: 'IL' },
    { name: 'Indiana', value: 'IN' },
    { name: 'Iowa', value: 'IA' },
    { name: 'Kansas', value: 'KS' },
    { name: 'Kentucky', value: 'KY' },
    { name: 'Louisiana', value: 'LA' },
    { name: 'Maine', value: 'ME' },
    { name: 'Maryland', value: 'MD' },
    { name: 'Massachusetts', value: 'MA' },
    { name: 'Michigan', value: 'MI' },
    { name: 'Minnesota', value: 'MN' },
    { name: 'Mississippi', value: 'MS' },
    { name: 'Missouri', value: 'MO' },
    { name: 'Montana', value: 'MT' },
    { name: 'Nebraska', value: 'NE' },
    { name: 'Nevada', value: 'NV' },
    { name: 'New Hampshire', value: 'NH' },
    { name: 'New Jersey', value: 'NJ' },
    { name: 'New Mexico', value: 'NM' },
    { name: 'New York', value: 'NY' },
    { name: 'North Carolina', value: 'NC' },
    { name: 'North Dakota', value: 'ND' },
    { name: 'Ohio', value: 'OH' },
    { name: 'Oklahoma', value: 'OK' },
    { name: 'Oregon', value: 'OR' },
    { name: 'Pennsylvania', value: 'PA' },
    { name: 'Rhode Island', value: 'RI' },
    { name: 'South Carolina', value: 'SC' },
    { name: 'South Dakota', value: 'SD' },
    { name: 'Tennessee', value: 'TN' },
    { name: 'Texas', value: 'TX' },
    { name: 'Utah', value: 'UT' },
    { name: 'Vermont', value: 'VT' },
    { name: 'Virginia', value: 'VA' },
    { name: 'Washington', value: 'WA' },
    { name: 'West Virginia', value: 'WV' },
    { name: 'Wisconsin', value: 'WI' },
    { name: 'Wyoming', value: 'WY' },
];

function WisePayment({ closePaymentPopup, onSuccess, type }) {
    const [address, setAddress] = useState('');
    const [state, setState] = useState('');
    const [city, setCity] = useState('');
    const [zip, setZip] = useState('');
    const [routingNumber, setRoutingNumber] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [accountType, setAccountType] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [showConfirmPopup, setShowConfirmPopup] = useState(false);
    const [userId, setUserId] = useState('');

    useEffect(() => {
        try {
            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            const uid = Usermeta_Data.split(':')[0];
            const uname = Usermeta_Data.split(':')[1];
            setUserId(uid);
            // Pre-fill address info if available
            userService.GetUserAccountInfo(uid, uname).then(apidata => {
                try {
                    if (apidata && apidata.length > 0 && apidata[0].code !== '-1') {
                        setAddress(apidata[0].address1 || '');
                        setState(apidata[0].stateName || '');
                        setCity(apidata[0].cityName || '');
                        setZip(apidata[0].zipCode || '');
                    }
                } catch (_) {}
            });
        } catch (error) {
            console.error('Error getting user info:', error);
        }
    }, []);

    const validateForm = () => {
        const result = wisePaymentSchema.safeParse({
            address, city, state, zip, routingNumber, accountNumber, accountType,
        });
        if (!result.success) {
            const fe = result.error.flatten().fieldErrors;
            setErrors({
                ...(fe.address?.[0] && { address: fe.address[0] }),
                ...(fe.city?.[0] && { city: fe.city[0] }),
                ...(fe.state?.[0] && { state: fe.state[0] }),
                ...(fe.zip?.[0] && { zip: fe.zip[0] }),
                ...(fe.routingNumber?.[0] && { routingNumber: fe.routingNumber[0] }),
                ...(fe.accountNumber?.[0] && { accountNumber: fe.accountNumber[0] }),
                ...(fe.accountType?.[0] && { accountType: fe.accountType[0] }),
            });
            return false;
        }
        setErrors({});
        return true;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        if (type === 'edit') {
            setShowConfirmPopup(true);
        } else {
            saveAddress();
        }
    };

    const saveAddress = async () => {
        setLoading(true);
        setErrors({});
        try {
            const apidata = await userService.SaveAddress(
                userId,
                address,
                'United States',
                state,
                city,
                zip,
                routingNumber,
                accountNumber,
                accountType
            );
            if (apidata && apidata.length > 0 && apidata[0].code !== '-1') {
                setLoading(false);
                if (onSuccess) onSuccess();
                else if (closePaymentPopup) closePaymentPopup();
            } else {
                setErrors({ saveSubmit: apidata?.[0]?.message || 'Failed to save payment information' });
                setLoading(false);
            }
        } catch (error) {
            console.error('Error saving payment:', error);
            setErrors({ saveSubmit: 'An error occurred. Please try again.' });
            setLoading(false);
        }
    };

    const confirmSubmit = async () => {
        setConfirmLoading(true);
        setErrors({});
        try {
            const apidata = await userService.SaveEditAddress(
                userId,
                address,
                'United States',
                state,
                city,
                zip,
                routingNumber,
                accountNumber,
                accountType
            );

            if (apidata && apidata.length > 0 && apidata[0].code !== '-1') {
                setConfirmLoading(false);
                setShowConfirmPopup(false);
                if (onSuccess) onSuccess();
                else if (closePaymentPopup) closePaymentPopup();
            } else {
                setErrors({ saveSubmit: apidata?.[0]?.message || 'Failed to save payment information' });
                setConfirmLoading(false);
                setShowConfirmPopup(false);
            }
        } catch (error) {
            console.error('Error saving payment:', error);
            setErrors({ saveSubmit: 'An error occurred. Please try again.' });
            setConfirmLoading(false);
            setShowConfirmPopup(false);
        }
    };

    return (
        <>
            <Dialog open={true} onOpenChange={closePaymentPopup}>
                <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto p-0">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b">
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-foreground mb-1">
                                {type === 'edit' ? 'Edit your bank account' : 'Link your bank account'}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                {type === 'edit'
                                    ? 'Add a new bank account to get payments credited directly! NuLifeSpan Rx uses Wise to securely create your payment account and does not store your bank details.'
                                    : 'Link your bank account to get payments credited directly! NuLifeSpan Rx uses Wise to securely create your payment account and does not store your bank details.'
                                }
                            </p>
                        </div>
                        <img src={LinkAccountImg} alt="Link Account" className="w-16 h-16 flex-shrink-0" />
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                        {/* Address */}
                        <div className="space-y-1">
                            <Label htmlFor="address">Address</Label>
                            <Input
                                id="address"
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                placeholder=" "
                                className={errors.address ? 'border-destructive' : ''}
                            />
                            {errors.address && <p className="text-xs text-destructive">{errors.address}</p>}
                        </div>

                        {/* City + State */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <Label htmlFor="city">City*</Label>
                                <Input
                                    id="city"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder=" "
                                    className={errors.city ? 'border-destructive' : ''}
                                />
                                {errors.city && <p className="text-xs text-destructive">{errors.city}</p>}
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="state">State*</Label>
                                <Select value={state} onValueChange={setState}>
                                    <SelectTrigger className={errors.state ? 'border-destructive' : ''}>
                                        <SelectValue placeholder="State" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {US_STATES.map(s => (
                                            <SelectItem key={s.value} value={s.name}>{s.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.state && <p className="text-xs text-destructive">{errors.state}</p>}
                            </div>
                        </div>

                        {/* Zip */}
                        <div className="space-y-1">
                            <Label htmlFor="zip">Zip/Postal Code*</Label>
                            <Input
                                id="zip"
                                value={zip}
                                onChange={(e) => setZip(e.target.value.replace(/\D/g, ''))}
                                placeholder=" "
                                maxLength={5}
                                className={errors.zip ? 'border-destructive' : ''}
                            />
                            {errors.zip && <p className="text-xs text-destructive">{errors.zip}</p>}
                        </div>

                        {/* Bank Account Number */}
                        <div className="space-y-1">
                            <Label htmlFor="accountNumber">Bank Account Number*</Label>
                            <Input
                                id="accountNumber"
                                value={accountNumber}
                                onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                                placeholder=" "
                                maxLength={17}
                                className={errors.accountNumber ? 'border-destructive' : ''}
                            />
                            {errors.accountNumber && <p className="text-xs text-destructive">{errors.accountNumber}</p>}
                        </div>

                        {/* Routing Number */}
                        <div className="space-y-1">
                            <Label htmlFor="routingNumber">Bank Routing Number*</Label>
                            <Input
                                id="routingNumber"
                                value={routingNumber}
                                onChange={(e) => setRoutingNumber(e.target.value.replace(/\D/g, ''))}
                                placeholder=" "
                                maxLength={9}
                                className={errors.routingNumber ? 'border-destructive' : ''}
                            />
                            {errors.routingNumber && <p className="text-xs text-destructive">{errors.routingNumber}</p>}
                        </div>

                        {/* Account Type */}
                        <div className="space-y-1">
                            <Label htmlFor="accountType">Account Type*</Label>
                            <Select value={accountType} onValueChange={setAccountType}>
                                <SelectTrigger className={errors.accountType ? 'border-destructive' : ''}>
                                    <SelectValue placeholder="Account Type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="CHECKING">CHECKING</SelectItem>
                                    <SelectItem value="SAVINGS">SAVINGS</SelectItem>
                                </SelectContent>
                            </Select>
                            {errors.accountType && <p className="text-xs text-destructive">{errors.accountType}</p>}
                        </div>

                        {/* Submit Error */}
                        {errors.saveSubmit && (
                            <p className="text-sm text-destructive">{errors.saveSubmit}</p>
                        )}

                        {/* Submit Button */}
                        <div className="pt-2">
                            <Button
                                type="submit"
                                variant="secondary"
                                className="w-full"
                                disabled={loading}
                            >
                                {loading ? (
                                    <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving</>

                                ) : (
                                    'Save & Continue'
                                )}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Confirmation Popup */}
            {showConfirmPopup && (
                <ConfirmPopup
                    open={showConfirmPopup}
                    onClose={() => setShowConfirmPopup(false)}
                    confirmYes={confirmSubmit}
                    loading={confirmLoading}
                    UserId={userId}
                    Address={address}
                    Country="United States"
                    State={state}
                    City={city}
                    Zip={zip}
                    RoutingNumber={routingNumber}
                    AccountNumber={accountNumber}
                    AccountType={accountType}
                />
            )}
        </>
    );
}

export default WisePayment;

